import { APIProvider } from "@vis.gl/react-google-maps";
import { useState } from "react";
import { useLocation } from "react-router-dom";

import Loading from "@Components/Loading";
import useGetDealers from "@Hooks/useDealers";
import useGetDeals from "@Hooks/useDeals";

import CountySelect from "./Filters/CountySelect";
import DealerSelect from "./Filters/DealerSelect";
import MonthYearSelect from "./Filters/MonthYearSelect";
import StateSelect from "./Filters/StateSelect";
import DataMap from "./Map";

import styles from "./styles.module.scss";

const DealerMap = () => {
  return (
    <>
      {process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? (
        <APIProvider
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          onLoad={() => console.log("Maps API has loaded.")}
          onError={(error) => console.error("Maps API error:", error)}
        >
          <MapComponent />
        </APIProvider>
      ) : (
        "No api key found"
      )}
    </>
  );
};

const MapComponent = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const dealerId = searchParams.get("id");

  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(
    dealerId ?? null,
  );
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [selectedCounty, setSelectedCounty] = useState<string | null>(null);

  const handleStateSelect = (state: string | null) => {
    setSelectedState(state);
  };
  const handleCountySelect = (county: string | null) => {
    setSelectedCounty(county);
  };
  const { data: dealers, isLoading: dealersLoading } = useGetDealers({
    ...(selectedState
      ? {
          query: {
            "data.info.state": selectedState,
          },
        }
      : {}),
  });
  const { data: deals, isLoading: dealsLoading } = useGetDeals({
    ...(selectedDealerId || selectedState
      ? {
          query: {
            ...(selectedDealerId
              ? { "data.dealershipId": selectedDealerId }
              : {}),
            ...(selectedState
              ? { "data.applicant.data.info.currentState": selectedState }
              : {}),
            ...(selectedCounty
              ? {
                  "data.applicant.data.info.currentCounty": selectedCounty,
                }
              : {}),
          },
          options: {
            limit: 999999999999999,
            projection: {
              "data.applicant.data.info.coordinates": 1,
              "data.applicant.data.info.firstName": 1,
              "data.applicant.data.info.lastName": 1,
              "data.applicant.data.info.currentState": 1,
              "data.applicant.data.info.currentCounty": 1,
              "data.info.dealDates.fundedAt": 1,
              "data.dealership.data.info.name": 1,
              "data.dealership.data.info.displayName": 1,
              "data.info.vehicle.year": 1,
              "data.info.vehicle.make": 1,
              "data.info.vehicle.model": 1,
              "data.info.payment.dealTotal": 1,
              "data.dealershipId": 1,
            },
          },
        }
      : {
          options: {
            limit: 999999999999999,
            projection: {
              "data.applicant.data.info.coordinates": 1,
              "data.applicant.data.info.firstName": 1,
              "data.applicant.data.info.lastName": 1,
              "data.applicant.data.info.currentState": 1,
              "data.applicant.data.info.currentCounty": 1,
              "data.dealership.data.info.name": 1,
              "data.dealership.data.info.displayName": 1,
              "data.info.dealDates.fundedAt": 1,
              "data.info.vehicle.year": 1,
              "data.info.vehicle.make": 1,
              "data.info.vehicle.model": 1,
              "data.info.payment.dealTotal": 1,
              "data.dealershipId": 1,
            },
          },
        }),
  });
  const isLoading = dealersLoading || dealsLoading;

  const [filterType, setFilterType] = useState<"monthly" | "yearly" | "all">(
    "all",
  );
  const [selectedMonth, setSelectedMonth] = useState<number>(
    new Date().getMonth(),
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear(),
  );

  const handleDateSelect = (month: number | null | undefined, year: number) => {
    if (month) setSelectedMonth(month);
    setSelectedYear(year);
  };

  const handleClearAllFilters = () => {
    setSelectedDealerId(dealerId ?? null);
    setSelectedState(null);
    setSelectedCounty(null);
    setFilterType("all");
    setSelectedMonth(new Date().getMonth());
    setSelectedYear(new Date().getFullYear());
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.headerTitle}>Dealers & Loans Map</h2>
      </div>
      <div className={styles.filters}>
        <div className={styles.filterGroup}>
          <h2>Filters</h2>

          <div className={styles.filterSectionMargin}>
            <label className={styles.filterLabel}>State</label>
            <StateSelect stateId={selectedState} onSelect={handleStateSelect} />
          </div>
          {selectedState && (
            <div className={styles.filterSection}>
              <label className={styles.filterLabel}>County</label>
              <CountySelect
                county={selectedCounty}
                state={selectedState}
                onSelect={handleCountySelect}
              />
            </div>
          )}

          {!dealerId && (
            <div className={styles.filterSection}>
              <label className={styles.filterLabel}>Dealer</label>
              <DealerSelect
                dealerId={selectedDealerId}
                onSelect={(id: string | null) => setSelectedDealerId(id)}
              />
            </div>
          )}

          <div className={styles.filterDiv}>
            <label className={styles.filterLabel}>Time Period</label>
            <div className={styles.filterButtons}>
              <button
                className={` ${styles.filterButton} ${
                  filterType === "monthly" ? styles.active : ""
                }`}
                onClick={() => setFilterType("monthly")}
              >
                Monthly
              </button>
              <button
                className={` ${styles.filterButton} ${
                  filterType === "yearly" ? styles.active : ""
                }`}
                onClick={() => setFilterType("yearly")}
              >
                Yearly
              </button>
              <button
                className={`${styles.filterButton} ${
                  filterType === "all" ? styles.active : ""
                }`}
                onClick={() => setFilterType("all")}
              >
                All Time
              </button>
            </div>
          </div>
          <div className={styles.filterSection}>
            <label className={styles.filterLabel}>Date</label>

            <MonthYearSelect
              onSelect={handleDateSelect}
              mode={filterType}
              month={selectedMonth}
              year={selectedYear}
            />
          </div>
          <button
            className={` ${styles.filterButton}`}
            onClick={handleClearAllFilters}
          >
            Clear All
          </button>
        </div>
      </div>

      {isLoading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            textAlign: "center",
            justifyContent: "center",
            marginTop: "100px",
            padding: 20,
            overflow: "hidden",
          }}
        >
          <Loading text="Loading data" />
        </div>
      ) : (
        <>
          {process.env.REACT_APP_GOOGLE_MAPS_API_KEY && !isLoading ? (
            <DataMap
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              filterType={filterType}
              selectedDealerId={selectedDealerId}
              dealers={dealers}
              deals={deals}
            />
          ) : null}
        </>
      )}
    </div>
  );
};

export default DealerMap;
